import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { CompensationComponentEditModal } from "@/pages/settings/compensation-components/widgets/CompensationComponentEditModal.tsx";
import { CompensationComponentsSortModal } from "@/pages/settings/compensation-components/widgets/CompensationComponentsSortModal.tsx";
import {
  useCompensationComponentArchive,
  useCompensationComponents,
} from "@/queries/use-compensation-components.query.ts";
import {
  ApiCompensationComponent,
  canArchiveCompensationComponent,
} from "@/types/compensation-component.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  Stack,
  Table,
  TableCell,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

const flagKeys = [
  "is_included_in_gross",
  "is_payable_to_employee",
  "is_included_in_total_cost",
  "is_off_payroll",
  "is_statutory_base",
] as const;

export function CompensationComponents() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { data: components, isLoading, isFetching } = useCompensationComponents();
  const archive = useCompensationComponentArchive();
  useTitle(t("core:compensation_types"), t("common:settings"));
  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.compensationTypes.viewed);
  });

  const nextSort = useMemo(
    () => Math.max(0, ...(components?.map((component) => component.sort) ?? [])) + 10,
    [components]
  );
  const openEditor = useCallback(
    (component?: ApiCompensationComponent) =>
      openModal(CompensationComponentEditModal, { component, nextSort }),
    [nextSort, openModal]
  );
  const handleArchive = useCallback(
    (component: ApiCompensationComponent) => {
      if (!canArchiveCompensationComponent(component)) return;
      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("core:archive_compensation_component_confirm"),
        acceptTitle: t("common:archive"),
        onAccept: async () => {
          await archive.mutateAsync(component.id);
          analyticsService.trackEvent(analyticEvents.compensationTypes.archived, {
            [analyticProperties.id]: component.id,
          });
        },
      });
    },
    [archive, openModal, t]
  );
  const columns = useMemo<ColumnDef<ApiCompensationComponent>[]>(
    () => [
      {
        header: t("common:title"),
        accessorKey: "title",
        cell: ({ row }) => <TableCell>{row.original.title}</TableCell>,
        ...ColumnSizes.employee,
      },
      {
        header: t("core:compensation_component_kind"),
        accessorKey: "kind",
        cell: ({ row }) => (
          <TableCell variant="secondary">
            {t(`core:compensation_component_kind_${row.original.kind}`)}
          </TableCell>
        ),
      },
      ...flagKeys.map((flag): ColumnDef<ApiCompensationComponent> => ({
        header: t(`core:compensation_component_${flag}`),
        accessorKey: flag,
        cell: ({ row }) => (
          <TableCell variant="secondary">
            {t(row.original[flag] ? "common:yes" : "common:no")}
          </TableCell>
        ),
      })),
    ],
    [t]
  );
  const renderRowAction = useCallback(
    (row: Row<ApiCompensationComponent>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => openEditor(row.original)}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
          title={t("common:edit")}
        />
        {canArchiveCompensationComponent(row.original) && (
          <DropdownMenuItem
            onClick={() => handleArchive(row.original)}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-box-archive" />}
            variant="menuDanger"
            title={`${t("common:archive")}...`}
          />
        )}
      </DropdownMenu>
    ),
    [handleArchive, openEditor, t]
  );

  return (
    <Page>
      <Page.Header showBack title={t("core:compensation_types")} navigateTo="/settings/employment">
        <Stack direction="horizontal" gap="sm">
          {(components?.length ?? 0) > 1 && (
            <Button variant="secondary" onClick={() => openModal(CompensationComponentsSortModal)}>
              {t("common:reorder")}
            </Button>
          )}
          <Button
            onClick={() => openEditor()}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:create_new")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        <Table
          renderRowAction={renderRowAction}
          data={components ?? []}
          columns={columns}
          isFetching={isFetching || archive.isPending}
          showSkeleton={isLoading}
          notFoundComponent={<EmptyStateNothingFound />}
          emptyStateComponent={<EmptyState />}
        />
      </Page.Content>
    </Page>
  );
}
